import { Injectable } from '@angular/core';
import {
    IAccommodation,
    IGetBookingsStayResponse,
    IPrice,
    IPriceKeyword,
    IRule,
    IRulePenalty, ISectorStay, IXId
} from "libraries/service/resources/booking/interfaces/IGetBookings";
import { ICancellationKeyword } from "../cancellation-conditions.interface";
import { IPenalty, IValueOrTotal } from "libraries/service/resources/hotel/IResourceHotel";
import { RuleType } from "libraries/component/booking-information/components/booking-price-card/components/conditions-tab/ICondition";
import {IHotelQuoteDetail, IHotelQuoteDetails} from "../../../../service/resources/hotel/hotel-quotation";
import {
    IAccommodation as IAccommodationDetails,
} from "libraries/hotel/models/details";
import _cloneDeep from "lodash-es/cloneDeep";
import {EXRateType} from "../../../../interfaces/ITotal";

@Injectable()
export class CancellationConditionsService {

    private static indexByKeyReduce<T>(accumulator: { [key: string]: T }, [key, value]: [string, T]): { [key: string]: T } {
        return {
            ...(accumulator || {}),
            [key]: value
        };
    }

    private static compareDates(date1: string | undefined, date2: string | undefined, isBegin: boolean): number {
        if (!date1 && !date2) {
            return 0;
        }

        if (!date1) {
            return isBegin ? 1 : -1;
        }

        if (!date2) {
            return isBegin ? -1 : 1;
        }

        return new Date(date1).getTime() - new Date(date2).getTime();
    }

    private static flattenReduce<T>(accumulator: T[], current: T[]): T[] {
        return [...(accumulator || []), ...(current || [])];
    }

    public extractCancellationKeyword(prices: IPrice[]): ICancellationKeyword[] {
        return prices.map((price: IPrice) => {
            return {
                priceId: price.xId,
                xCode: (price.keyword || []).map((keyword: IPriceKeyword) => keyword.xCode),
            };
        });
    }

    public createPenaltiesByAccomodationFromRules(booking: IGetBookingsStayResponse, removeCostPenalties?: boolean, removeRetailPenalties?: boolean): { [xId: string]: IPenalty[] } {
        return (booking.accomodation || []).map((accomodation: IAccommodation): [string, IPenalty[]] => [
            accomodation.xId,
            this.getPenaltiesForAccomodation(booking, accomodation.xId, removeCostPenalties, removeRetailPenalties),
        ])
            .reduce(CancellationConditionsService.indexByKeyReduce, {});
    }

    public createPenaltiesByAccomodationFromQuoteDetails(details: IHotelQuoteDetails, removeCostPenalties?: boolean, removeRetailPenalties?: boolean): { [xId: string]: IPenalty[] } {
        return (Object.values(details) || []).map((detail: IHotelQuoteDetail): [string, IPenalty[]] => [
            detail.room.xId,
            this.getPenaltiesForAccomodationFromQuoteDetails(details, detail.room.xId, removeCostPenalties, removeRetailPenalties),
        ])
            .reduce(CancellationConditionsService.indexByKeyReduce, {});
    }

    public getPenaltiesForAccomodationFromQuoteDetails(details: IHotelQuoteDetails, accomodationId: string, removeCostPenalties?: boolean, removeRetailPenalties?: boolean): IPenalty[]{
        const penalties: IPenalty[] = [];
        Object.values(details)
            .filter((detail: IHotelQuoteDetail): boolean => detail.room.xId === accomodationId)
            .map((detail: IHotelQuoteDetail): void | [] => {
                if(!detail.room.penalty){
                    return [];
                }
                Object.values(detail.room.penalty).forEach((penalty: IPenalty): void => {
                    if(penalty.total.retail || Object.values(penalty.total.cost).some((cost: number): boolean => cost.toString() !== "0")) {
                        penalties.push({...penalty, xType: EXRateType.cost});
                        if(penalty.total.retail){
                            const penaltyClone: IPenalty = _cloneDeep(penalty);
                            penaltyClone.total.cost = penalty.total.retail;
                            penalties.push({...penaltyClone, xType: EXRateType.retail});
                        }
                    }
                });
            });

        return penalties;
    }

    public getPenaltiesForAccomodation(booking: IGetBookingsStayResponse, accomodationId?: string | undefined, removeCostPenalties?: boolean, removeRetailPenalties?: boolean): IPenalty[] | undefined {
        const whenLastToCancelWithoutPenalty: string = booking.whenLastToCancelWithoutPenalty;
        const penalties: IPenalty[] = (booking.rule || [])
            .filter((rule: IRule) => Boolean(rule) && rule.xType === RuleType.cancellation)
            .filter((rule: IRule) => {
                return (!accomodationId && !rule.accomodation)
                    || (accomodationId && rule.accomodation && rule.accomodation.xId === accomodationId);
            })
            .filter((rule: IRule) => rule.penalty && rule.penalty.length)
            .map((rule: IRule): IPenalty[] => rule.penalty
                .filter((rulePenalty: IRulePenalty) => rulePenalty.validity &&
                    rulePenalty.validity.cancellation &&
                    rulePenalty.validity.cancellation.end !== booking.whenLastToCancelWithoutPenalty)
                .map((rulePenalty: IRulePenalty): IPenalty => {
                    return {
                        ...rulePenalty,
                        total: this.getPenaltyTotal(rulePenalty, booking, accomodationId),
                        validity: rulePenalty.validity && rulePenalty.validity.cancellation,
                        priceId: (rule.price || {}).xId
                    };
                })
            )
            .reduce(CancellationConditionsService.flattenReduce, [])
            .filter((penalty: IPenalty) => penalty.validity)
            .filter((penalty: IPenalty) => removeCostPenalties ? penalty.xType && penalty.xType !== "cost" : true)
            .filter((penalty: IPenalty) => removeRetailPenalties ? penalty.xType && penalty.xType !== "retail" : true)
            .sort((penalty1: IPenalty, penalty2: IPenalty) => {
                return CancellationConditionsService.compareDates(penalty1.validity.end, penalty2.validity.end, true)
                    || CancellationConditionsService.compareDates(penalty1.validity.begin, penalty2.validity.begin, false);
            });

        return penalties.length ? penalties : undefined;
    }

    private getPenaltyTotal(rulePenalty: IRulePenalty, booking: IGetBookingsStayResponse, accomodationId: string | undefined): IValueOrTotal {
        if (!rulePenalty.unitValue || !rulePenalty.currency) {
            return undefined;
        }

        if (!accomodationId) {
            return {cost: {[rulePenalty.currency]: +rulePenalty.unitValue * this.getAccomodationQuantity(booking, undefined)}};
        }

        return {
            cost: {
                [rulePenalty.currency]: +rulePenalty.unitValue * this.getAccomodationQuantity(booking, accomodationId)
            }
        };
    }

    private getAccomodationQuantity(booking: IGetBookingsStayResponse, accommodationId: string | undefined): number {
        const sectors: ISectorStay[] = (booking.sector || [])
            .filter((sector: ISectorStay) =>
                !accommodationId
                || (sector.accomodation
                    && sector.accomodation.some((reference: IXId) => reference.xId === accommodationId)
                ));

        return Math.min(
            1,
            sectors
                .map((sector: ISectorStay): number => (sector.quantity && sector.quantity.room) || 1)
                .filter(Boolean)
                .reduce((current: number, sectorQuantity: number) => current + sectorQuantity, 0)
        );
    }


}